import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import classNames from 'classnames';
import css from './GalleryView.module.css';

const GalleryView = ({ media, currentIndex, onClose, onNavigate, onSetIndex }) => {
    const intl = useIntl();
    const currentMedia = media[currentIndex];

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'ArrowLeft') {
                onNavigate('prev');
            } else if (e.key === 'ArrowRight') {
                onNavigate('next');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, onNavigate]);

    if (!currentMedia) return null;

    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < media.length - 1;

    return (
        <div className={css.overlay} onClick={onClose}>
            <div className={css.container} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className={css.header}>
                    <div className={css.counter}>
                        {currentIndex + 1} / {media.length}
                    </div>
                    <button className={css.closeButton} onClick={onClose} aria-label={intl.formatMessage({ id: 'GalleryView.close' })}>
                        <i className="ri-close-fill"></i>
                    </button>
                </div>

                {/* Main Media */}
                <div className={css.mainContent}>
                    <button
                        className={classNames(css.navButton, css.prevButton, { [css.disabled]: !canGoPrev })}
                        onClick={() => canGoPrev && onNavigate('prev')}
                        disabled={!canGoPrev}
                        aria-label={intl.formatMessage({ id: 'GalleryView.previous' })}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>

                    <div className={css.mediaContainer}>
                        {currentMedia.type === 'video' ? (
                            <figure className={css.lightboxFigure}>
                                <video
                                    src={currentMedia.secure_url || currentMedia.playback_url}
                                    aria-label={currentMedia.seoTags?.altText || 'Store gallery video'}
                                    title={currentMedia.seoTags?.titleTag || ''}
                                    className={css.media}
                                    controls
                                    autoPlay
                                />
                                <figcaption className={css.lightboxCaption}>
                                    {currentMedia.seoTags.caption}
                                </figcaption>
                            </figure>
                        ) : (
                            <figure className={css.lightboxFigure}>
                                <img
                                    src={currentMedia.secure_url}
                                    alt={currentMedia.seoTags?.altText || 'Store gallery photo'}
                                    title={currentMedia.seoTags?.titleTag || ''}
                                    className={css.media}
                                    loading="eager"
                                    fetchpriority="high"
                                />
                                {currentMedia.seoTags?.caption && (
                                    <figcaption className={css.lightboxCaption}>
                                        {currentMedia.seoTags.caption}
                                    </figcaption>
                                )}
                            </figure>
                        )}
                    </div>

                    <button
                        className={classNames(css.navButton, css.nextButton, { [css.disabled]: !canGoNext })}
                        onClick={() => canGoNext && onNavigate('next')}
                        disabled={!canGoNext}
                        aria-label={intl.formatMessage({ id: 'GalleryView.next' })}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>

                {/* Thumbnail Strip */}
                {media.length > 1 && (
                    <div className={css.thumbnailStrip}>
                        {media.map((item, index) => (
                            <button
                                key={item.id}
                                className={classNames(css.thumbnail, {
                                    [css.activeThumbnail]: index === currentIndex,
                                })}
                                onClick={() => {
                                    if (index !== currentIndex && onSetIndex) {
                                        onSetIndex(index);
                                    }
                                }}
                            >
                                {item.type === 'video' ? (
                                    <video src={item.secure_url} className={css.thumbnailImage} muted />
                                ) : (
                                    <img src={item.secure_url} alt="" className={css.thumbnailImage} />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GalleryView;

